// Pure audio math — no Web Audio, no DOM, no React. Unit-testable in node/jsdom.
// These functions are the framework-agnostic core that survives a React Native
// port unchanged, and they encode the exact PCM contract the Flask WS expects:
//   uplink   = Int16 LE PCM, 16 kHz mono   (mic -> Gemini)
//   downlink = Int16 LE PCM, 24 kHz mono   (Gemini -> playback), base64 in JSON

/** Float32 [-1,1] samples -> Int16 LE PCM, clamped. */
export function floatToPcm16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    let s = input[i];
    if (s > 1) s = 1;
    else if (s < -1) s = -1;
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

/** Int16 PCM -> Float32 [-1,1]. */
export function pcm16ToFloat32(input: Int16Array): Float32Array {
  const out = new Float32Array(input.length);
  for (let i = 0; i < input.length; i++) out[i] = input[i] / 0x8000;
  return out;
}

/** Decode a base64 string of Int16 LE PCM into an Int16Array. */
export function base64ToInt16(b64: string): Int16Array {
  const bin = atob(b64); // present in browsers and the jsdom test env
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  // Int16Array view requires byte length to be even; pad defensively.
  const usable = bytes.length - (bytes.length % 2);
  return new Int16Array(bytes.buffer, 0, usable / 2);
}

/** Root-mean-square amplitude of a frame — drives the noise gate. */
export function rms(frame: Float32Array): number {
  if (frame.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < frame.length; i++) sum += frame[i] * frame[i];
  return Math.sqrt(sum / frame.length);
}
